import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Users, Folder, Calendar, ClipboardList, MessageSquare, FileText,
  Settings, MapPin, Award, BookOpen, Bell, BarChart3, Plus, Pencil, X, GripVertical
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Default quick links
const DEFAULT_LINKS = [
  { id: '1', label: 'Team', icon: 'Users', path: '/company-team', color: 'bg-blue-100 text-blue-600' },
  { id: '2', label: 'Spaces', icon: 'Folder', path: '/spaces', color: 'bg-purple-100 text-purple-600' },
  { id: '3', label: 'Bookings', icon: 'Calendar', path: '/bookings', color: 'bg-green-100 text-green-600' },
  { id: '4', label: 'Checklists', icon: 'ClipboardList', path: '/checklists', color: 'bg-orange-100 text-orange-600' },
  { id: '5', label: 'Chat', icon: 'MessageSquare', path: '/chat', color: 'bg-pink-100 text-pink-600' },
  { id: '6', label: 'Posts', icon: 'FileText', path: '/posts', color: 'bg-cyan-100 text-cyan-600' },
];

// Icon mapping
const ICON_MAP = {
  Users,
  Folder,
  Calendar,
  ClipboardList,
  MessageSquare,
  FileText,
  Settings,
  MapPin,
  Award,
  BookOpen,
  Bell,
  BarChart3,
};

export function QuickLinksTile({ filter, size, useMockData, onConfigChange }) {
  const navigate = useNavigate();
  const [links, setLinks] = useState(filter?.links || DEFAULT_LINKS);
  const [isEditing, setIsEditing] = useState(false);

  // Update links when filter changes
  useEffect(() => {
    if (filter?.links) {
      setLinks(filter.links);
    }
  }, [filter?.links]);

  const handleLinkClick = (path) => {
    if (!isEditing) {
      navigate(path);
    }
  };

  const handleRemoveLink = (linkId) => {
    const newLinks = links.filter(l => l.id !== linkId);
    setLinks(newLinks);
    onConfigChange?.({ ...filter, links: newLinks });
  };

  const isCompact = size === 'small';

  return (
    <div className="h-full flex flex-col">
      {/* Header with edit toggle */}
      <div className="flex items-center justify-between mb-2 shrink-0">
        <span className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">
          Quick Access
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? <X className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
        </Button>
      </div>

      {/* Links grid */}
      <ScrollArea className="flex-1 min-h-0">
        <div className={cn(
          "grid gap-2 pr-2",
          isCompact ? "grid-cols-2" : "grid-cols-3"
        )}>
          {links.map((link) => {
            const Icon = ICON_MAP[link.icon] || Folder;

            return (
              <div
                key={link.id}
                onClick={() => handleLinkClick(link.path)}
                className={cn(
                  "relative group p-2 rounded-lg border transition-all text-center",
                  isEditing ? "bg-gray-50 border-dashed" : "bg-white hover:shadow-md cursor-pointer"
                )}
              >
                {isEditing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveLink(link.id);
                    }}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
                <div className={cn(
                  "mx-auto rounded-lg flex items-center justify-center mb-1",
                  link.color,
                  isCompact ? "w-8 h-8" : "w-10 h-10"
                )}>
                  <Icon className={isCompact ? "h-4 w-4" : "h-5 w-5"} />
                </div>
                <p className="text-[10px] font-medium text-gray-700 truncate">
                  {link.label}
                </p>
              </div>
            );
          })}

          {/* Add new link button (in edit mode) */}
          {isEditing && (
            <div className="p-2 rounded-lg border border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 hover:border-gray-400 cursor-pointer transition-colors">
              <Plus className={isCompact ? "h-4 w-4" : "h-5 w-5"} />
              <p className="text-[10px] mt-1">Add</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
